import React, { useState, useEffect, useCallback, useRef } from "react";
import { Monitor, RefreshCcwIcon, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/store/hooks";
import { IRootReducerState } from "@/store/reducers";
import ConditionalTooltipComponent from "@/components/common-components/conditional-tooltip/ConditionalTooltipComponent.tsx";
import "./UrlSearchBarComponent.scss";

interface UrlSearchBarComponentProps {
    baseUrl?: string;
    onUrlChange?: (url: string) => void;
    onNavigateToRoute?: (route: string) => void;
    onReload?: () => void;
    onOpenInNewTab?: () => void;
    isLoading?: boolean;
    className?: string;
}

const UrlSearchBarComponent = ({
    baseUrl = "",
    onUrlChange,
    onNavigateToRoute,
    onReload,
    onOpenInNewTab,
    isLoading = false,
    className
}: UrlSearchBarComponentProps) => {
    const [currentRoute, setCurrentRoute] = useState('');
    const [childRoute, setChildRoute] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<Array<{ value: string; label: string; type: string }>>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Get detected routes from Redux store
    const detectedRoutes = useAppSelector((state: IRootReducerState) => state.projectData.detectedRoutes);

    // Listen for route changes from iframe
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.data?.type === 'ROUTE_CHANGE') {
                setChildRoute(event.data.path);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    // Update current route when child route changes
    useEffect(() => {
        if (childRoute) {
            setCurrentRoute(childRoute);
            setInputValue(childRoute.startsWith('/') ? childRoute : `/${childRoute}`);
        }
    }, [childRoute]);

    // Set suggestions without filtering
    useEffect(() => {
        if (detectedRoutes?.suggestions) {
            setFilteredSuggestions(detectedRoutes.suggestions);
        }
    }, [detectedRoutes?.suggestions]);

    // Debounced effect for input changes
    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (inputValue !== currentRoute) {
                onUrlChange?.(inputValue);
            }
        }, 300);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [inputValue, currentRoute, onUrlChange]);

    // Helper function to remove dt parameter from route
    const removeDtParameter = useCallback((route: string) => {
        if (route.includes('?dt=')) {
            const urlParts = route.split('?');
            const baseRoute = urlParts[0];
            const queryParams = urlParts[1];
            
            if (queryParams) {
                // Remove dt parameter from query string
                const params = new URLSearchParams(queryParams);
                params.delete('dt');
                const remainingParams = params.toString();
                
                // Reconstruct the route
                return remainingParams ? `${baseRoute}?${remainingParams}` : baseRoute;
            } else {
                return baseRoute;
            }
        }
        return route;
    }, []);

    const getDisplayRoute = useCallback(() => {
        const route = removeDtParameter(currentRoute);
        
        // Only show the route after the first /
        if (route.startsWith('/')) {
            return route;
        }
        return route ? `/${route}` : '/';
    }, [currentRoute, removeDtParameter]);

    const handleNavigateToRoute = useCallback((route: string) => {
        const cleanRoute = route.startsWith('/') ? route.slice(1) : route;
        setCurrentRoute(cleanRoute);
        setInputValue(route);
        onNavigateToRoute?.(cleanRoute);
        setIsEditing(false);
    }, [onNavigateToRoute]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleNavigateToRoute(inputValue);
            inputRef.current?.blur();
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setInputValue(getDisplayRoute());
            inputRef.current?.blur();
        }
    }, [inputValue, handleNavigateToRoute, getDisplayRoute]);

    const getInputDisplayValue = useCallback(() => {
        if (isEditing) {
            return inputValue;
        }
        return getDisplayRoute();
    }, [isEditing, inputValue, getDisplayRoute]);

    const getFullUrl = () => {
        const baseRoute = currentRoute.startsWith('/') ? currentRoute : `/${currentRoute}`;
        const route = removeDtParameter(baseRoute);
        return baseUrl + route;
    };

    const handleContainerClick = useCallback(() => {
        if (!isEditing) {
            setIsEditing(true);
            setInputValue(getDisplayRoute());
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
        }
    }, [isEditing, getDisplayRoute]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
    };

    const handleSuggestionClick = (suggestion: { value: string; label: string; type: string }) => {
        setInputValue(suggestion.value);
        setShowSuggestions(false);
        handleNavigateToRoute(suggestion.value);
    };

    const handleInputFocus = () => {
        if (detectedRoutes?.suggestions && detectedRoutes.suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleInputBlur = () => {
        // Delay hiding suggestions to allow clicking on them
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200);
    };

    return (
        <div className={cn("url-search-bar", className)}>
            <div className="url-input-container" onClick={handleContainerClick}>
                {/* Monitor/Phone Icon */}
                <div className="monitor-icon">
                    <Monitor className="w-4 h-4" />
                </div>
                
                {/* Route Display/Input */}
                <div className="route-display">
                    {isEditing ? (
                        <input
                            ref={inputRef}
                            type="text"
                            value={getInputDisplayValue()}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className="route-input"
                            autoComplete="off"
                            spellCheck={false}
                        />
                    ) : (
                        <span className="route-text">
                            {getDisplayRoute() || "/"}
                        </span>
                    )}
                </div>
                
                {/* Action Buttons */}
               
                <div className="action-buttons">
                    {/* External Link Button */}
                    {baseUrl && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const fullUrl = getFullUrl();
                            if (fullUrl) {
                                window.open(fullUrl, '_blank', 'noopener,noreferrer');
                            }
                            onOpenInNewTab?.();
                        }}
                        className="action-btn"
                        title="Open in new tab"
                        type="button"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                    )}
                    {/* Refresh Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onReload?.();
                        }}
                        className="action-btn"
                        disabled={isLoading}
                        title={isLoading ? "Reloading..." : "Reload"}
                        type="button"
                    >
                        <RefreshCcwIcon className={cn("w-3.5 h-3.5", isLoading && "animate-spin")} />
                    </button>
                </div>
            </div>
            
            {/* Route Suggestions Dropdown */}
            {showSuggestions && filteredSuggestions.length > 0 && (
                <div ref={suggestionsRef} className="route-suggestions">
                    <div className="suggestions-list">
                        {filteredSuggestions.map((suggestion, index) => (
                            <div
                                key={index}
                                className="suggestion-item"
                                onClick={() => handleSuggestionClick(suggestion)}
                            >
                                <ConditionalTooltipComponent
                                    text={suggestion.value}
                                    className="suggestion-route ellipsis-text"
                                    side="right"
                                >
                                    <span>{suggestion.value}</span>
                                </ConditionalTooltipComponent>
                            </div>
                        ))}
                    </div>
                </div>
            )}  
        </div>
    );
};

export default UrlSearchBarComponent;
